import { isEmpty, sanitizeValues } from '@intelligence/v3/utils/common';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IRegionSelectControlProps, IRegionSelectMiddlewareParams } from './types';

const SPECIAL_MARKETS = ['global', 'global_wo_china'];
const SINGLE_SELECT_DEFAULT_MARKET = 'us';

export const getNormalizedRegionSelectMarkets = (value?: unknown, options?: IRegion[] | null): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedValues = sanitizeValues(
    value.filter((item): item is string => typeof item === 'string').map((item) => item.toLowerCase()),
  );

  if (isEmpty(normalizedValues)) {
    return [];
  }

  const countryCodes = getAllCountryCodesByRegions(options);

  if (isEmpty(countryCodes)) {
    return normalizedValues;
  }

  const countryCodeSet = new Set(countryCodes.map((item) => item.toLowerCase()));

  return normalizedValues.filter((item) => {
    return SPECIAL_MARKETS.includes(item) || countryCodeSet.has(item);
  });
};

export const normalizeRegionSelectValueByItem = (item: IFilterItemConfig, value?: unknown): string[] => {
  const controlProps = item?.controlProps as IRegionSelectControlProps | undefined;
  const isSingleSelect = Boolean(controlProps?.singleSelect);
  const defaultMarket = isSingleSelect ? SINGLE_SELECT_DEFAULT_MARKET : 'global';

  if (isEmpty(value)) {
    return [defaultMarket];
  }

  const middlewareParams = getRegionSelectMiddlewareParams(item);
  let validCountryCodes = getNormalizedRegionSelectMarkets(value, middlewareParams?.options);

  if (isSingleSelect) {
    validCountryCodes = validCountryCodes.filter((code) => !SPECIAL_MARKETS.includes(code));

    if (validCountryCodes.length > 1) {
      validCountryCodes = [validCountryCodes[0]];
    }
  }

  if (isEmpty(validCountryCodes)) {
    return [defaultMarket];
  }

  return validCountryCodes;
};

export const getRegionSelectMiddlewareParams = (item?: IFilterItemConfig | null): IRegionSelectMiddlewareParams => {
  if (isEmpty(item)) {
    return { options: [] };
  }

  const controlProps = item?.controlProps as IRegionSelectControlProps | undefined;
  const options = controlProps?.options as IRegion[] | null;

  return { options };
};
